import { createActor } from 'xstate';
import { gameMachine } from './gameMachine';
import { STARTING_HEALTH, STARTING_HAND_SIZE } from '../constants';
import { EventBus } from '../events/EventBus';
/**
 * XState-powered Game Engine
 * Unified architecture: both human and AI players are treated as just players
 * AI players get their moves via AIDecisionEngine
 */
export class XStateGameEngine {
    actor;
    eventBus;
    playerTypes = new Map();
    constructor() {
        this.actor = createActor(gameMachine);
        this.eventBus = new EventBus();
    }
    // ============ Game Lifecycle ============
    startGame(player1, player2) {
        this.playerTypes.set(player1.id, player1.type ?? 'human');
        this.playerTypes.set(player2.id, player2.type ?? 'human');
        // Create full player objects
        const fullPlayer1 = {
            id: player1.id,
            name: player1.name,
            health: STARTING_HEALTH,
            magicka: 1,
            maxMagicka: 1,
            runes: [25, 20, 15, 10, 5],
            clues: 0,
            hand: player1.deck.slice(0, STARTING_HAND_SIZE),
            deck: player1.deck.slice(STARTING_HAND_SIZE),
        };
        const fullPlayer2 = {
            id: player2.id,
            name: player2.name,
            health: STARTING_HEALTH,
            magicka: 1,
            maxMagicka: 1,
            runes: [25, 20, 15, 10, 5],
            clues: 0,
            hand: player2.deck.slice(0, STARTING_HAND_SIZE),
            deck: player2.deck.slice(STARTING_HAND_SIZE),
        };
        this.actor.start();
        this.actor.send({
            type: 'START_GAME',
            player1: fullPlayer1,
            player2: fullPlayer2,
        });
    }
    // ============ Player Type Queries ============
    isAIPlayer(playerId) {
        return this.playerTypes.get(playerId) === 'ai';
    }
    getCurrentPlayer() {
        const state = this.getGameState();
        return state.players[state.currentPlayerIndex] ?? null;
    }
    getCurrentPlayerId() {
        const player = this.getCurrentPlayer();
        return player?.id ?? null;
    }
    // ============ Game Actions ============
    playCard(playerId, cardId, position) {
        const snapshot = this.actor.getSnapshot();
        const canPlay = this.canPlayCard(playerId, cardId, position);
        if (!canPlay.success) {
            return canPlay;
        }
        this.actor.send({
            type: 'PLAY_CARD',
            playerId,
            cardId,
            position,
        });
        // Emit event for UI
        const player = snapshot.context.players.find(p => p.id === playerId);
        const card = player?.hand.find(c => c.id === cardId);
        if (card) {
            this.eventBus.emit({
                type: 'CREATURE_SUMMONED',
                source: playerId,
                target: cardId,
                data: { card },
            });
        }
        return { success: true };
    }
    applyItemOrSpell(playerId, cardId, targetCreatureId) {
        const snapshot = this.actor.getSnapshot();
        const player = snapshot.context.players.find(p => p.id === playerId);
        if (!player)
            return { success: false, message: 'Player not found' };
        const card = player.hand.find(c => c.id === cardId);
        if (!card)
            return { success: false, message: 'Card not in hand' };
        if (card.cost > player.magicka)
            return { success: false, message: 'Not enough energy' };
        this.actor.send({
            type: 'APPLY_ITEM',
            playerId,
            cardId,
            targetCreatureId,
        });
        if (card.type === 'item') {
            this.eventBus.emit({
                type: 'ITEM_EQUIPPED',
                source: playerId,
                target: targetCreatureId,
                data: { item: card },
            });
        }
        return { success: true };
    }
    attack(playerId, attackerId, targetId) {
        const snapshot = this.actor.getSnapshot();
        // Check if target is a creature or player
        const targetCreature = this.findCreatureOnBoard(snapshot.context, targetId);
        const targetPlayer = snapshot.context.players.find(p => p.id === targetId);
        if (targetCreature) {
            this.actor.send({
                type: 'ATTACK_CREATURE',
                playerId,
                attackerId,
                targetId,
            });
        }
        else if (targetPlayer) {
            this.actor.send({
                type: 'ATTACK_PLAYER',
                playerId,
                attackerId,
                targetPlayerId: targetId,
            });
        }
        else {
            return { success: false, message: 'Invalid target' };
        }
        return { success: true };
    }
    endTurn(playerId) {
        const snapshot = this.actor.getSnapshot();
        const currentPlayer = snapshot.context.players[snapshot.context.currentPlayerIndex];
        if (currentPlayer?.id !== playerId) {
            return { success: false, message: 'Not your turn' };
        }
        this.actor.send({
            type: 'END_TURN',
            playerId,
        });
        return { success: true };
    }
    // ============ State Getters ============
    getGameState() {
        const snapshot = this.actor.getSnapshot();
        const ctx = snapshot.context;
        return {
            id: ctx.gameId,
            players: ctx.players,
            currentPlayerIndex: ctx.currentPlayerIndex,
            turn: ctx.turn,
            phase: this.getPhase(snapshot),
            board: ctx.board,
            winner: ctx.winner,
        };
    }
    getPlayer(playerId) {
        const snapshot = this.actor.getSnapshot();
        return snapshot.context.players.find(p => p.id === playerId);
    }
    getPlayerCreatures(playerId) {
        const snapshot = this.actor.getSnapshot();
        const playerIndex = snapshot.context.players.findIndex(p => p.id === playerId);
        const lane = playerIndex === 0
            ? snapshot.context.board.playerLane
            : snapshot.context.board.opponentLane;
        return lane.filter((c) => c !== null);
    }
    getOpponentHand(playerId) {
        const snapshot = this.actor.getSnapshot();
        const opponent = snapshot.context.players.find(p => p.id !== playerId);
        return opponent?.hand.length ?? 0;
    }
    // ============ Event Bus ============
    getEventBus() {
        return this.eventBus;
    }
    // ============ Private Helpers ============
    canPlayCard(playerId, cardId, position) {
        const snapshot = this.actor.getSnapshot();
        const ctx = snapshot.context;
        const currentPlayer = ctx.players[ctx.currentPlayerIndex];
        if (currentPlayer?.id !== playerId) {
            return { success: false, message: 'Not your turn' };
        }
        const player = ctx.players.find(p => p.id === playerId);
        if (!player)
            return { success: false, message: 'Player not found' };
        const card = player.hand.find(c => c.id === cardId);
        if (!card)
            return { success: false, message: 'Card not in hand' };
        if (card.cost > player.magicka)
            return { success: false, message: 'Not enough energy' };
        if (card.type !== 'creature')
            return { success: false, message: 'Only creatures can be played to board' };
        const playerIndex = ctx.players.findIndex(p => p.id === playerId);
        const lane = playerIndex === 0 ? ctx.board.playerLane : ctx.board.opponentLane;
        if (lane[position] !== null)
            return { success: false, message: 'Position occupied' };
        return { success: true };
    }
    findCreatureOnBoard(ctx, creatureId) {
        for (const lane of [ctx.board.playerLane, ctx.board.opponentLane]) {
            const creature = lane.find(c => c?.id === creatureId);
            if (creature)
                return creature;
        }
        return null;
    }
    getPhase(snapshot) {
        const state = snapshot.value;
        if (state === 'idle')
            return 'waiting';
        if (state === 'gameOver')
            return 'ended';
        return 'playing';
    }
    // ============ Cleanup ============
    destroy() {
        this.actor.stop();
    }
}
